import { connect, FormikValues, getIn } from 'formik'
import { useEffect } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { string } from 'yup'

import { Radio } from '@/components/Form'
import SelectComponent from '@/components/Form/Select'
import { FormLabel, Row } from '@/components/StyledComponents'
import { StyledDivider, StyledRadioInput } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import {
  DataStorageActions,
  DataStorageExpiration,
  DataStorageOperations,
  DataStorageStepData,
  FieldTypes,
  StepData
} from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

//<editor-fold desc="Styled Components">
// Bit lame - Select proptypes don't play nice with TS types
const Select: any = SelectComponent

const StyledRadio = styled(Radio)`
  margin-top: 1rem;
  margin-bottom: 1.5rem !important;
  display: flex;
  flex-direction: row;
  .form_field {
    display: flex;
  }
  @media (max-width: 600px) {
    flex-direction: column;
  }
`

const StyledRadioTab = styled.button<any>`
  background: none;
  color: inherit;
  border: none;
  font: inherit;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1rem;
  box-shadow: rgb(0 0 0 / 20%) 1px 1px 6px;
  flex: 1 0 50%;
  cursor: ${({ disabled }: any) => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: 0.25rem;

  ${props =>
    props.isSelected &&
    css`
      box-shadow: 0 0 0 2px var(--wg-blue-link);
      background-color: #f6f6f6;
    `};
`

const StyledRow = styled(Row)`
  justify-content: flex-start;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type DataStorageProps = {
  readOnly?: boolean
}
//</editor-fold>

const getValidationSchema = (intl: IntlShape, formObject: any) => {
  const result: any = {
    operation: string().required(intl.formatMessage(messages.operationRequired)),
    action: string().required(intl.formatMessage(messages.actionTypeRequired)),
    objectName: string().trim().required(intl.formatMessage(messages.dataRequired))
  }

  if (
    formObject.action === DataStorageActions.REPLACE ||
    formObject.action === DataStorageActions.APPEND ||
    formObject.action === DataStorageActions.GET_BY_KEY ||
    formObject.action === DataStorageActions.DELETE_BY_KEY
  ) {
    result.keyIdentifier = string().trim().required(intl.formatMessage(messages.keyIdentifierRequired))
  }

  if (formObject.action === DataStorageActions.REPLACE || formObject.action === DataStorageActions.APPEND) {
    result.data = string().trim().required(intl.formatMessage(messages.dataToStoreRequired))
  }

  return result
}

const getInitialValues = (stepData?: DataStorageStepData): StepData => ({
  operation: stepData?.operation ?? DataStorageOperations.MODIFY,
  action: stepData?.action ?? DataStorageActions.APPEND,
  objectName: stepData?.objectName ?? '',
  keyIdentifier: stepData?.keyIdentifier ?? '',
  data: stepData?.data ?? '',
  expiresAfter: stepData?.expiresAfter ?? DataStorageExpiration.THREE_MONTHS
})
const getDataStorageOperationHelpUrl = 'https://help.workgrid.com/s/article/Utilizing-the-data-storage-step'
const DataStorageOperation = ({ formik, readOnly = false }: DataStorageProps & FormikValues) => {
  const intl = useIntl()

  const { setFieldValue } = formik
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()

  useEffect(() => {
    if (getIn(formik.values, 'stepData.operation') === 'write') {
      setFieldValue(`stepData.operation`, DataStorageOperations.MODIFY)
    }
  })

  return (
    <>
      <StyledRow>
        <FormLabel>{intl.formatMessage(messages.operation)}</FormLabel>
      </StyledRow>
      <StyledRadio name="type" disabled={readOnly}>
        <StyledRadioTab
          disabled={readOnly}
          isSelected={
            getIn(formik.values, 'stepData.operation') === DataStorageOperations.MODIFY ||
            getIn(formik.values, 'stepData.operation') === 'write'
          }
          onClick={async () => {
            await setFieldValue('stepData', {
              operation: DataStorageOperations.MODIFY,
              action: DataStorageActions.APPEND,
              keyIdentifier: '',
              objectName: formik.values?.stepData?.objectName || '',
              data: ''
            })
          }}
        >
          <StyledRadioInput
            disabled={readOnly}
            id="stepData.operation.modify"
            name="stepData.operation"
            label={intl.formatMessage(messages.modifyData)}
            value={DataStorageOperations.MODIFY}
            labelLocation="after"
          />
        </StyledRadioTab>
        <StyledRadioTab
          disabled={readOnly}
          isSelected={getIn(formik.values, 'stepData.operation') === DataStorageOperations.READ}
          onClick={async () => {
            await setFieldValue('stepData', {
              operation: DataStorageOperations.READ,
              action: DataStorageActions.GET_ALL,
              keyIdentifier: '',
              objectName: formik.values?.stepData?.objectName || '',
              data: ''
            })
          }}
        >
          <StyledRadioInput
            disabled={readOnly}
            id="stepData.operation.read"
            name="stepData.operation"
            label={intl.formatMessage(messages.readData)}
            value={DataStorageOperations.READ}
            labelLocation="after"
          />
        </StyledRadioTab>
      </StyledRadio>
      {getIn(formik.values, 'stepData.operation') && (
        <>
          <Select
            disabled={readOnly}
            label={intl.formatMessage(messages.action)}
            name="stepData.action"
            description={intl.formatMessage(
              getIn(formik.values, 'stepData.operation') === DataStorageOperations.MODIFY ||
                getIn(formik.values, 'stepData.operation') === 'write'
                ? getIn(formik.values, 'stepData.action') === DataStorageActions.REPLACE
                  ? messages.replaceDesc
                  : getIn(formik.values, 'stepData.action') === DataStorageActions.APPEND
                    ? messages.appendDesc
                    : getIn(formik.values, 'stepData.action') === DataStorageActions.DELETE_ALL
                      ? messages.deleteAllRecordsDesc
                      : messages.deleteRecordByKeyDesc
                : getIn(formik.values, 'stepData.action') === DataStorageActions.GET_BY_KEY
                  ? messages.getRecordByKeyDesc
                  : messages.getAllRecordsDesc
            )}
            descriptionPosition="bottom"
            variant=""
            isplaceholder={!getIn(formik.values, 'stepData.action')}
            onChange={(e: any) => {
              if (e.target.name !== 'stepData.action') return
              setFieldValue('stepData.action', e.target.value)
            }}
            required={true}
            defaultValue={
              getIn(formik.values, 'stepData.operation') === DataStorageOperations.READ
                ? DataStorageActions.GET_ALL
                : DataStorageActions.APPEND
            }
          >
            {getIn(formik.values, 'stepData.operation') === DataStorageOperations.READ ? (
              <>
                <option value={DataStorageActions.GET_ALL}>{intl.formatMessage(messages.getAllRecords)}</option>
                <option value={DataStorageActions.GET_BY_KEY}>
                  {intl.formatMessage(messages.getRecordByKeyIdentifier)}
                </option>
              </>
            ) : (
              <>
                <option value={DataStorageActions.APPEND}>{intl.formatMessage(messages.append)}</option>
                <option value={DataStorageActions.REPLACE}>{intl.formatMessage(messages.replace)}</option>
                <option value={DataStorageActions.DELETE_ALL}>
                  {intl.formatMessage(messages.deletingByStorageName)}
                </option>
                <option value={DataStorageActions.DELETE_BY_KEY}>
                  {intl.formatMessage(messages.deletingByKeyIdentifier)}
                </option>
              </>
            )}
          </Select>
          <StyledDivider />
          <VariableInput
            className="mt-6"
            readOnly={readOnly}
            label={intl.formatMessage(messages.storageName)}
            name="stepData.objectName"
            placeholder={intl.formatMessage(messages.dataDescPlaceholder)}
            required={true}
            variables={variables}
            validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
          />

          {(getIn(formik.values, 'stepData.action') === DataStorageActions.GET_BY_KEY ||
            getIn(formik.values, 'stepData.action') === DataStorageActions.APPEND ||
            getIn(formik.values, 'stepData.action') === DataStorageActions.REPLACE ||
            getIn(formik.values, 'stepData.action') === DataStorageActions.DELETE_BY_KEY) && (
            <VariableInput
              readOnly={readOnly}
              label={intl.formatMessage(messages.keyIdentifier)}
              name="stepData.keyIdentifier"
              placeholder={intl.formatMessage(messages.keyIdentifierPlaceholder)}
              required
              variables={variables}
              validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            />
          )}
          {(getIn(formik.values, 'stepData.action') === DataStorageActions.APPEND ||
            getIn(formik.values, 'stepData.action') === DataStorageActions.REPLACE) && (
            <>
              <VariableInput
                readOnly={readOnly}
                label={intl.formatMessage(messages.dataToStore)}
                name="stepData.data"
                placeholder={intl.formatMessage(messages.dataToStorePlaceholder)}
                required={getIn(formik.values, 'stepData.operation') === DataStorageOperations.MODIFY}
                variables={variables}
              />
              <Select
                disabled={readOnly}
                label={intl.formatMessage(messages.expiresAfter)}
                name="stepData.expiresAfter"
                description="Specify the length of time the record is held in storage until it is deleted."
                descriptionPosition="top"
                variant=""
                isplaceholder={!getIn(formik.values, 'stepData.expiresAfter')}
                onChange={(e: any) => setFieldValue('stepData.expiresAfter', e.target.value)}
                defaultValue={DataStorageExpiration.THREE_MONTHS}
              >
                <>
                  <option value={DataStorageExpiration.LIFETIME}>Never</option>
                  <option value={DataStorageExpiration.ONE_DAY}>1 day</option>
                  <option value={DataStorageExpiration.ONE_WEEK}>1 week</option>
                  <option value={DataStorageExpiration.ONE_MONTH}>1 month</option>
                  <option value={DataStorageExpiration.THREE_MONTHS}>3 months</option>
                  <option value={DataStorageExpiration.SIX_MONTHS}>6 months</option>
                  <option value={DataStorageExpiration.ONE_YEAR}>1 year</option>
                </>
              </Select>
              {getIn(formik.values, 'stepData.expiresAfter') === DataStorageExpiration.LIFETIME ? (
                <p className="text-gray-500">
                  * Note that in the event of an app deletion, the data will still be deleted.
                </p>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  )
}
export default connect(DataStorageOperation)

export { getValidationSchema, getInitialValues, getDataStorageOperationHelpUrl }
